'use client';

import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Fab,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { Iconify } from 'src/components/iconify';
import { PERSONAS } from 'src/auth/context/dev/dev-auth-provider';
import {
  ärCrashlyticsTillgängligt,
  tvingaKraschTest,
  rapporteraTillCrashlytics,
} from 'src/lib/crashlytics';
import {
  DEV_LÄGE_AKTIVT,
  SCENARIER,
  sättScenario,
  sättRoller,
  useScenario,
  useDevRoller,
  type Scenario,
} from 'src/lib/dev-lage';

// ----------------------------------------------------------------------

const SCENARIO_ETIKETT: Record<Scenario, string> = {
  data: 'Data',
  laddar: 'Laddar',
  tom: 'Tomt',
  fel: 'Fel',
  langsam: 'Långsamt',
};

const SCENARIO_HJÄLP: Record<Scenario, string> = {
  data: 'Verkliga anrop mot backend.',
  laddar: 'Anropen svarar aldrig — visar laddtillståndet.',
  tom: 'Svaren är tomma — visar tomtillståndet.',
  fel: 'Anropen ger 500 — visar feltillståndet.',
  langsam: 'Svaren fördröjs — avslöjar hopp och blinkningar.',
};

/**
 * Granskningspanelen för utvecklingsläget.
 *
 * Två reglage, och båda finns av samma skäl: en vy är inte polerad förrän den
 * setts i alla sina tillstånd och genom varje behörighet som når den.
 *
 * Renderar ingenting alls utanför dev-läget, och `DEV_LÄGE_AKTIVT` kräver ett
 * icke-produktionsbygge, så den kan inte följa med ut.
 */
export function DevPanel() {
  const [öppen, setÖppen] = useState(false);
  // Båda läses ur modulstoren, som ger serverrenderingen standardvärdet.
  const scenario = useScenario();
  const aktivaRollnamn = useDevRoller();

  if (!DEV_LÄGE_AKTIVT) {
    return null;
  }

  const väljScenario = (nytt: Scenario | null) => {
    if (!nytt) return;
    sättScenario(nytt);
    // Vyerna hämtar vid montering — ladda om så scenariot faktiskt slår igenom.
    window.location.reload();
  };

  return (
    <>
      <Tooltip title="Granskningsläge" placement="left">
        <Fab
          size="small"
          color="secondary"
          aria-label="Öppna granskningsläget"
          onClick={() => setÖppen(true)}
          sx={{ position: 'fixed', right: 16, bottom: 16, zIndex: (theme) => theme.zIndex.speedDial }}
        >
          <Iconify icon="solar:test-tube-bold" width={20} />
        </Fab>
      </Tooltip>

      <Drawer
        anchor="right"
        open={öppen}
        onClose={() => setÖppen(false)}
        slotProps={{ paper: { sx: { width: { xs: 1, sm: 360 } } } }}
      >
        <Stack sx={{ height: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              px: 2.5,
              py: 2,
              borderBottom: (theme) => `solid 1px ${theme.vars.palette.divider}`,
            }}
          >
            <Iconify icon="solar:test-tube-bold" width={20} sx={{ color: 'secondary.main' }} />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Granskningsläge
            </Typography>
            <Chip size="small" variant="soft" color="secondary" label="dev" />
          </Stack>

          <Box sx={{ p: 2.5, overflowY: 'auto' }}>
            <Typography variant="overline" sx={{ color: 'text.disabled', letterSpacing: 1 }}>
              Svarstillstånd
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={scenario}
              onChange={(_, värde) => väljScenario(värde)}
              sx={{ mt: 1, flexWrap: 'wrap' }}
              aria-label="Välj svarstillstånd"
            >
              {SCENARIER.map((s) => (
                <ToggleButton key={s} value={s}>
                  {SCENARIO_ETIKETT[s]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {SCENARIO_HJÄLP[scenario]}
            </Typography>

            <Divider sx={{ my: 3, borderStyle: 'dashed' }} />

            <Typography variant="overline" sx={{ color: 'text.disabled', letterSpacing: 1 }}>
              Behörighet
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Sidomenyn och varje grind byggs om för den roll du väljer.
            </Typography>

            <List disablePadding>
              {PERSONAS.map((persona) => {
                const vald =
                  persona.roller.length === aktivaRollnamn.length &&
                  persona.roller.every((r) => aktivaRollnamn.includes(r));

                return (
                  <ListItemButton
                    key={persona.nyckel}
                    selected={vald}
                    onClick={() => sättRoller(persona.roller)}
                    sx={{ borderRadius: 1, mb: 0.5 }}
                  >
                    <ListItemText
                      primary={persona.etikett}
                      secondary={persona.beskrivning}
                      slotProps={{
                        primary: { variant: 'subtitle2' },
                        secondary: { variant: 'caption' },
                      }}
                    />
                    {vald && (
                      <Iconify
                        icon="solar:check-circle-bold"
                        width={20}
                        sx={{ color: 'secondary.main', flexShrink: 0 }}
                      />
                    )}
                  </ListItemButton>
                );
              })}
            </List>

            {ärCrashlyticsTillgängligt() && (
              <>
                <Divider sx={{ my: 3, borderStyle: 'dashed' }} />
                <Typography variant="overline" sx={{ color: 'text.disabled', letterSpacing: 1 }}>
                  Firebase Crashlytics
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                  Testa felrapportering på denna mobilenhet.
                </Typography>
                <Stack spacing={1}>
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    onClick={() => {
                      void rapporteraTillCrashlytics(new Error('Testfelet loggat till Crashlytics (icke-fatalt)'));
                    }}
                  >
                    Logga icke-fatalt fel
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => {
                      void tvingaKraschTest('Tvingad krasch från Flinky DevPanel');
                    }}
                  >
                    Tvinga krasch (Fatalt)
                  </Button>
                </Stack>
              </>
            )}
          </Box>
        </Stack>
      </Drawer>
    </>
  );
}
